import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Minus, Trash2, Utensils } from "lucide-react";
import { fmtMoney } from "@/lib/finance";
import { toast } from "sonner";

export const DeductionsPanel = ({ deductions, totalDrawn, onCreate, onDelete }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState("");
  const [dateVal, setDateVal] = useState(today);
  const [note, setNote] = useState("");

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid deduction amount"); return; }
    const iso = new Date(`${dateVal}T12:00:00Z`).toISOString();
    await onCreate({ amount: amt, deduction_datetime: iso, category: "Daily Draw", note: note || null });
    toast.success("Deduction logged — netted from compounding");
    setAmount(""); setNote("");
  };

  return (
    <div className="glass rounded-xl border border-[#232D40] overflow-hidden" data-testid="deductions-panel">
      <div className="px-5 py-4 border-b border-[#232D40] flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Utensils className="h-4 w-4 text-rose-400" /> Daily Deductions
          </h2>
          <p className="text-xs text-slate-500">Draws you take out — netted from the 5% engine</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Drawn</p>
          <p className="font-mono-data font-bold text-rose-400" data-testid="total-drawn">{fmtMoney(totalDrawn, 0)}</p>
        </div>
      </div>

      <div className="p-4 flex flex-wrap items-end gap-2 border-b border-[#232D40]">
        <div className="flex-1 min-w-[120px]">
          <Input data-testid="deduction-amount-input" type="number" placeholder="Amount $"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="bg-[#0B0E17] border-[#232D40] font-mono-data h-10" />
        </div>
        <div className="w-[150px]">
          <Input data-testid="deduction-date-input" type="date" value={dateVal}
            onChange={(e) => setDateVal(e.target.value)}
            className="bg-[#0B0E17] border-[#232D40] font-mono-data h-10" />
        </div>
        <div className="flex-1 min-w-[120px]">
          <Input data-testid="deduction-note-input" placeholder="Note (optional)"
            value={note} onChange={(e) => setNote(e.target.value)}
            className="bg-[#0B0E17] border-[#232D40] h-10" />
        </div>
        <Button data-testid="deduction-submit-btn" onClick={submit}
          className="bg-rose-500 hover:bg-rose-400 text-white font-semibold h-10">
          <Minus className="h-4 w-4 mr-1" strokeWidth={2.6} />Log Draw
        </Button>
      </div>

      <div className="divide-y divide-[#232D40] max-h-[220px] overflow-y-auto">
        {deductions.length === 0 && (
          <div className="p-6 text-center text-slate-500 text-sm">No deductions logged.</div>
        )}
        {deductions.map((d) => (
          <div key={d.id} data-testid={`deduction-row-${d.id}`} className="px-5 py-3 flex items-center justify-between">
            <div>
              <p className="font-mono-data font-semibold text-rose-300">−{fmtMoney(d.amount, 0)}</p>
              <p className="text-xs text-slate-500">
                {new Date(d.deduction_datetime).toISOString().slice(0, 10)}{d.note ? ` · ${d.note}` : ""}
              </p>
            </div>
            <button data-testid={`delete-deduction-${d.id}`} onClick={() => onDelete(d.id)}
              className="text-slate-600 hover:text-rose-400 p-1.5">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
